import React, {useEffect, useState} from 'react';
import { useSelector } from 'react-redux';
import {
    Alert,
    AppBar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
    Zoom,Switch, List,
  ListItem,
  ListItemButton,
  ListItemText,Toolbar,Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Assessment as StatsIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckIcon,
    Close as CloseIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Filter as FilterIcon,
    PlayArrow as StartIcon,
    Quiz as QuizIcon,
    Save as SaveIcon,
    Timer as TimerIcon,
    TrendingUp as TrendingUpIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import {
  
  ArrowBack,
  ArrowForward,
  NavigateBefore,
  NavigateNext
} from '@mui/icons-material';

import {createTheme, styled, ThemeProvider} from '@mui/material/styles';
import {api,selectSchoolDetails} from "../../../../common";

// Enhanced theme with modern colors and gradients
const theme = createTheme({
    palette: {
        primary: {
            main: '#6366f1',
            light: '#818cf8',
            dark: '#4f46e5',
        },
        secondary: {
            main: '#ec4899',
            light: '#f472b6',
            dark: '#db2777',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        success: {
            main: '#10b981',
            light: '#34d399',
        },
        warning: {
            main: '#f59e0b',
            light: '#fbbf24',
        },
        error: {
            main: '#ef4444',
            light: '#f87171',
        }
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 700,
            background: 'linear-gradient(45deg, #6366f1, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        }
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    }
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '8px 20px',
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                }
            }
        }
    }
});

// Styled components for enhanced UI
const GradientCard = styled(Card)(({theme}) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '& .MuiCardContent-root': {
        padding: theme.spacing(3),
    }
}));

const StatCard = styled(Card)(({theme, color}) => ({
    background: `linear-gradient(135deg, ${color}15 0%, ${color}25 100%)`,
    border: `2px solid ${color}20`,
    borderRadius: 20,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: `0 10px 30px ${color}30`,
    }
}));

const StyledAppBar = styled(AppBar)(({theme}) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
}));

// API Services
const ExamService = {
getExams: async (filters) => {
        try {
            const response = await api.get('/api/exam/mcq/list', {params: filters});
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch exams');
        }
    },

    getExamById: async (examId) => {
        try {
            const response = await api.get(`/api/exam/mcq/${examId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch exam');
        }
    },

    updateExam: async (examId, exam) => {
        try {
            const response = await api.put(`/api/exam/mcq/${examId}`, exam);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update exam');
        }
    },

    deleteExam: async (examId) => {
        try {
            const response = await api.delete(`/api/exam/mcq/${examId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to delete exam');
        }
    },

startExam: async (examId, studentId) => {
    try {
        const response = await api.post('/api/exam/mcq/start', {
            examId,
            studentId
        }); // 👈 send as JSON body, not params
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to start exam');
    }
},


    submitExam: async (submission) => {
        try {
            const response = await api.post('/api/exam/mcq/submit', submission);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to submit exam');
        }
    },

    getExamStatistics: async (examId) => {
        try {
            const response = await api.get(`/api/exam/mcq/${examId}/statistics`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
        }
    },

    getSubmissionsByExam: async (examId) => {
        try {
            const response = await api.get(`/api/exam/mcq/${examId}/submissions`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch submissions');
        }
    },

    getAllStatistics: async (schoolId) => {
        try {
            const response = await api.get(`/api/exam/mcq/statistics/summary`, {
                params: {schoolId}
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch summary statistics');
        }
    }
};

const QuestionService = {
   
    getQuestions: async (filters) => {
        try {
            const response = await api.get('/api/exam/mcq/questions', {params: filters});
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to fetch questions');
        }
    },

    updateQuestion: async (questionId, question) => {
        try {
            const response = await api.put(`/api/exam/mcq/question/${questionId}`, question);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update question');
        }
    },

    deleteQuestion: async (questionId) => {
        try {
            const response = await api.delete(`/api/exam/mcq/question/${questionId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to delete question');
        }
    }
};

function TabPanel({children, value, index, ...other}) {
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Fade in={value === index} timeout={500}>
                    <Box sx={{p: 3}}>{children}</Box>
                </Fade>
            )}
        </div>
    );
}

export default function EnhancedExamDashboard({ timeLeft: initialTimeLeft, }) {
         const classSections=useSelector((state)=>state.master.data.classSections)
    
          const userData = useSelector(selectSchoolDetails);
    
            const schoolId = userData?.id;
 const session = userData.session;
    const [currentTab, setCurrentTab] = useState(0);
    const [exams, setExams] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});
    const [statistics, setStatistics] = useState({
        totalExams: 0,
        totalQuestions: 0,
        totalSubmissions: 0,
        averageScore: 0
    });

    // Dialog states
    const [examDialog, setExamDialog] = useState({open: false, exam: null});
    const [questionDialog, setQuestionDialog] = useState({open: false, question: null});
    const [examTakingDialog, setExamTakingDialog] = useState({
        open: false,
        exam: null,
        answers: {},
        timeLeft: 0,
        currentQuestion: 0
    });


    // Form states
const [examForm, setExamForm] = useState({
  examName: '',
  subject: '',
  className: '',
  section: '',
  session: '',
  description: '',
  instructions: '',
  topic: '',
  examDate: '',
  startTime: '',
  endTime: '',
  durationMinutes: 60,
  totalMarks: 100,
  passingMarks: '',
  status: 'DRAFT',
  schoolId: schoolId,
  createdBy: userData?.id || '',
});


    const [questionForm, setQuestionForm] = useState({
        questionText: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: 'A',
        marks: 1,
        topic: '',
        subject: '',
        explanation: '',
        schoolId: 1
    });

    // Filters
    const [examFilters, setExamFilters] = useState({
        schoolId: schoolId,
        session:session ,
        className: '',
        section: '',
        subject: ''
    });

    const [questionFilters, setQuestionFilters] = useState({
          schoolId: schoolId,
        session:session ,
        className: '',
        section: '',
        subject: '',
    });

    // Timer ref for cleanup
    const [examTimer, setExamTimer] = useState(null);

    useEffect(() => {
        loadData();
        return () => {
            // Clean up timer if it exists
            if (examTimer) {
                clearInterval(examTimer);
            }
        };
    }, []);

    const loadData = async () => {
        await Promise.all([loadExams(), loadQuestions(), loadStatistics()]);
    };

    const loadExamsFilter = async () => {
        setLoading(true);
        try {
            const data = await ExamService.getExams(examFilters);
            setExams(data);
        } catch (error) {
            showSnackbar('Error loading exams: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };
   const loadExams = async () => {
        setLoading(true);
        try {
            const data = await ExamService.getExams(examFilters);
            setExams(data);
        } catch (error) {
            showSnackbar('Error loading exams: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

        const loadQuestionsFilter = async () => {
        try {
            const data = await QuestionService.getQuestions(questionFilters);
            setQuestions(data);
        } catch (error) {
            showSnackbar('Error loading questions: ' + error.message, 'error');
        }
    };
    const loadQuestions = async () => {
        try {
            const data = await QuestionService.getQuestions();
            setQuestions(data);
        } catch (error) {
            showSnackbar('Error loading questions: ' + error.message, 'error');
        }
    };

    useEffect(() => {
  loadQuestions(); // ✅ actually calls the function
}, []);


    const loadStatistics = async () => {
        try {
            const stats = await ExamService.getAllStatistics(1); // Assuming schoolId = 1
            setStatistics({
                totalExams: stats.totalExams || 0,
                totalQuestions: stats.totalQuestions || 0,
                totalSubmissions: stats.totalSubmissions || 0,
                averageScore: stats.averageScore || 0
            });
        } catch (error) {
            console.error('Error loading statistics:', error);
            // Use default values if API fails
            setStatistics({
                totalExams: exams.length,
                totalQuestions: questions?.length,
                totalSubmissions: 0,
                averageScore: 0
            });
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({open: true, message, severity});
    };


   const handleCreateExam = async () => {
  try {
    if (!userData?.id) {
      throw new Error('Missing required user information. Please login again.');
    }

    // Format date and time fields for backend
    const formattedExamDate = examForm.examDate ? 
      `${examForm.examDate}T00:00:00` : null;
    
    const formattedStartTime = examForm.startTime ? 
      `${examForm.examDate}T${examForm.startTime}:00` : null;
    
    const formattedEndTime = examForm.endTime ? 
      `${examForm.examDate}T${examForm.endTime}:00` : null;

    const payload = {
      examName: examForm.examName,
      subject: examForm.subject,
      className: examForm.className,
      section: examForm.section,
      session: examForm.session,
      description: examForm.description,
      instructions: examForm.instructions,
      topic: examForm.topic,
      examDate: formattedExamDate,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      durationMinutes: parseInt(examForm.durationMinutes),
      totalMarks: parseInt(examForm.totalMarks),
      passingMarks: parseInt(examForm.passingMarks) || 0,
      status: examForm.status,
      schoolId: parseInt(schoolId),
      createdBy: userData.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // API call to create exam
    const response = await api.post('/api/exam/mcq/create', payload);
    
    // Reset form to initial state
    setExamForm({
      examName: '',
      subject: '',
      className: '',
      section: '',
      session: '',
      description: '',
      instructions: '',
      topic: '',
      examDate: '',
      startTime: '',
      endTime: '',
      durationMinutes: 60,
      totalMarks: 100,
      passingMarks: '',
      status: 'DRAFT',
      schoolId: schoolId,
      createdBy: userData?.id || '',
    });
    
    // Show success message
    setSnackbar({
      open: true,
      message: "Exam created successfully!",
      severity: "success",
    });

  } catch (error) {
    setSnackbar({
      open: true,
      message: error.message || "Exam creation failed",
      severity: "error",
    });
  }
};
 const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
const handleCreateQuestion = async () => {
  try {
    if (!schoolId) {
      throw new Error("School ID is required");
    }

    // // Map correct answer letter to index
    // const answerMap = { A: 0, B: 1, C: 2, D: 3 };
    // const correctIndex = answerMap[questionForm.correctAnswer];
  let correctIndex;
    const raw = questionForm.correctAnswer;

    if (raw === null || raw === undefined || raw === "") {
      throw new Error("Please select the correct answer");
    }

    // if already a number
    if (typeof raw === "number") {
      correctIndex = raw;
    } else {
      const s = String(raw).trim();

      // numeric string like "0" or "1"
      if (/^\d+$/.test(s)) {
        correctIndex = parseInt(s, 10);
      } else {
        // letter mapping (A/B/C/D) -> 0/1/2/3
        const answerMap = { A: 0, B: 1, C: 2, D: 3 };
        correctIndex = answerMap[s.toUpperCase()];
      }
    }

    // validate index
    if (typeof correctIndex !== "number" || Number.isNaN(correctIndex)) {
      throw new Error("Invalid correct answer format");
    }
 

    // marks fallback
    const marks = Number.isFinite(Number(questionForm.marks))
      ? parseInt(questionForm.marks, 10)
      : 1;

  

      const payload = {
      questionText: questionForm.questionText || null,
         options: [
    questionForm.optionA,
    questionForm.optionB,
    questionForm.optionC,
    questionForm.optionD
  ].filter(Boolean),
      correctAnswer: correctIndex,
      explanation: questionForm.explanation || null,
      marks: 100,
      subject: questionForm.subject || null,
      topic: questionForm.topic || null,
      schoolId: parseInt(schoolId, 10),
      session: session,
      className: questionForm.className || 1,
      section: questionForm.section || "d",
      difficultyLevel: questionForm.difficultyLevel || "EASY", // ✅ default
      questionType: questionForm.questionType || "MULTIPLE_CHOICE", // ✅ default
      // optional: additional fields you may want to include
      negativeMarks: (questionForm.negativeMarks !== "" && questionForm.negativeMarks != 1)
        ? Number(questionForm.negativeMarks)
        : 0.0,
      timeLimitSeconds: questionForm.timeLimitSeconds ? Number(questionForm.timeLimitSeconds) : null,
      imageUrl: questionForm.imageUrl || null,
      createdBy: userData.name || null,
      approvedBy: userData.role || null,
      isApproved: !!questionForm.isApproved,
      isActive: !!questionForm.isActive,
      usageCount: Number(questionForm.usageCount) || 0,
      successRate: Number(questionForm.successRate) || 0.0,
      examMcq: questionForm.examMcq ? { id: questionForm.examMcq.id } : null
    };

    const response = await api.post(
      "/api/exam/mcq/question/create",
      payload
    );

    // Reset form and close dialog
    setQuestionForm({
      questionText: "",
      optionA: "",
      optionB: "",
      optionC: "",
      optionD: "",
      correctAnswer: "",
      explanation: "",
      marks: 1,
      subject: "",
      topic: "",
      schoolId: schoolId
    });

    setQuestionDialogOpen(false);

    setSnackbar({
      open: true,
      message: "Question created successfully!",
      severity: "success"
    });
  } catch (error) {
    setSnackbar({
      open: true,
      message: error.message || "Question creation failed",
      severity: "error"
    });
  }
};

// const [students, setExams] = useState([]);
const [loadingExam, setLoadingExam] = useState(true);
const [ExamError, setExamError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
 const [timerInterval, setTimerInterval] = useState(null);
useEffect(() => {
  const fetchExamList = async () => {
    try {
      const response = await api.get("/api/exam/mcq/list", {
        params: {
          schoolId: schoolId
        }
      });

      // ✅ Ensure we always store an array
      const examsData = Array.isArray(response.data)
        ? response.data
        : response.data.data || [];

      setExams(examsData);
    } catch (err) {
      setExamError(err.message);
    } finally {
      setLoadingExam(false);
    }
  };

  if (schoolId) {
    fetchExamList();
  } else {
    setLoadingExam(false);
    setExamError("School ID is required to fetch exams");
  }
}, [schoolId]);

const handleUpdateExam = async () => {
        if (!examForm.title || !examForm.subject || !examForm.className) {
            showSnackbar('Please fill in all required fields', 'error');
            return;
        }

        setLoading(true);
        try {
            const updatedExam = await ExamService.updateExam(examDialog.exam.id, examForm);
            setExams(prev => prev.map(e => e.id === examDialog.exam.id ? updatedExam : e));
            setExamDialog({open: false, exam: null});
            resetExamForm();
            showSnackbar('Exam updated successfully');
        } catch (error) {
            showSnackbar('Error updating exam: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

   

    const handleDeleteExam = async (examId) => {
        if (window.confirm('Are you sure you want to delete this exam?')) {
            setLoading(true);
            try {
                await ExamService.deleteExam(examId);
                setExams(prev => prev.filter(e => e.id !== examId));
                showSnackbar('Exam deleted successfully');
                loadStatistics(); // Refresh statistics
            } catch (error) {
                showSnackbar('Error deleting exam: ' + error.message, 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            setLoading(true);
            try {
                await QuestionService.deleteQuestion(questionId);
                setQuestions(prev => prev.filter(q => q.id !== questionId));
                showSnackbar('Question deleted successfully');
                loadStatistics(); // Refresh statistics
            } catch (error) {
                showSnackbar('Error deleting question: ' + error.message, 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleStartExam = async (examId) => {
        setLoading(true);
        try {
            // Clean up any existing timer
            if (examTimer) {
                clearInterval(examTimer);
            }

            const examData = await ExamService.startExam(examId, 'student123'); // Replace with actual student ID

            // Check if examData has questions before setting state
            if (!examData || !examData.questions) {
                throw new Error('Exam has no questions');
            }

            setExamTakingDialog({
                open: true,
                exam: examData,
                answers: {},
                timeLeft: examData.duration * 60,
                currentQuestion: 0
            });

            // Start timer
            const timer = setInterval(() => {
                setExamTakingDialog(prev => {
                    if (prev.timeLeft <= 1) {
                        clearInterval(timer);
                        handleSubmitExam();
                        return prev;
                    }
                    return {...prev, timeLeft: prev.timeLeft - 1};
                });
            }, 1000);

            setExamTimer(timer);
        } catch (error) {
            showSnackbar('Error starting exam: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitExam = async () => {
        // Clean up timer
        if (examTimer) {
            clearInterval(examTimer);
            setExamTimer(null);
        }

        setLoading(true);
        try {
            const submission = {
                examId: examTakingDialog.exam.id,
                studentId: 'student123', // Replace with actual student ID
                answers: examTakingDialog.answers,
                timeTaken: (examTakingDialog.exam.duration * 60) - examTakingDialog.timeLeft
            };

            const result = await ExamService.submitExam(submission);
            setExamTakingDialog({open: false, exam: null, answers: {}, timeLeft: 0, currentQuestion: 0});
            showSnackbar(`Exam submitted successfully! Score: ${result.score}/${result.totalMarks}`, 'success');
            loadStatistics(); // Refresh statistics
        } catch (error) {
            showSnackbar('Error submitting exam: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const resetExamForm = () => {
        setExamForm({
            title: '',
            subject: '',
            className: '',
            section: '',
            duration: 60,
            totalMarks: 100,
            description: '',
            schoolId: 1,
            session: session,
        });
    };

    const resetQuestionForm = () => {
        setQuestionForm({
            questionText: '',
            optionA: '',
            optionB: '',
            optionC: '',
            optionD: '',
            correctAnswer: '0',
            marks: 1,
            topic: '',
            subject: '',
            explanation: '',
            schoolId: 1
        });
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const openExamDialog = (exam = null) => {
        if (exam) {
            setExamForm({
                title: exam.title || '',
                subject: exam.subject || '',
                className: exam.className || '',
                section: exam.section || '',
                duration: exam.duration || 60,
                totalMarks: exam.totalMarks || 100,
                description: exam.description || '',
                schoolId: exam.schoolId || 1,
                session: exam.session || '2024-25'
            });
        } else {
            resetExamForm();
        }
        setExamDialog({open: true, exam});
    };

    const openQuestionDialog = (question = null) => {
        if (question) {
            setQuestionForm({
                questionText: question.questionText || '',
                optionA: question.optionA || '',
                optionB: question.optionB || '',
                optionC: question.optionC || '',
                optionD: question.optionD || '',
                correctAnswer: ['A', 'B', 'C', 'D'][question.correctAnswer] || 'A',
                marks: question.marks || 1,
                topic: question.topic || '',
                subject: question.subject || '',
                explanation: question.explanation || '',
                schoolId: question.schoolId || 1
            });
        } else {
            resetQuestionForm();
        }
        setQuestionDialog({open: true, question});
    };


  // Timer effect
 useEffect(() => {
    if ( timeLeft > 0) {
      // Clear any existing interval
      if (timerInterval) clearInterval(timerInterval);
      
      // Set up new interval
      const interval = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            clearInterval(interval);
            handleSubmitExam();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      
      setTimerInterval(interval);
      
      // Cleanup on unmount
      return () => clearInterval(interval);
    }
  }, [ timeLeft]);



  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };



  const handleQuestionSelect = (index) => {
    setCurrentQuestion(index);
  };

  const isAnswered = (questionId) => {
    return answers.hasOwnProperty(questionId);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < (questions?.length || 0) - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };
const currentQuestionData = questions && questions[currentQuestion];
  const questionsCount = questions?.length || 0;
  // Safe access


    return (
        <ThemeProvider theme={theme}>
            <Box sx={{flexGrow: 1, minHeight: '100vh', backgroundColor: 'background.default'}}>
                <Container maxWidth="xl" sx={{mt: 4, pb: 4}}>
                    {/* Statistics Cards */}
                    <Grid container spacing={3} sx={{mb: 4}}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Zoom in={true} timeout={500}>
                                <StatCard color="#6366f1">
                                    <CardContent sx={{textAlign: 'center'}}>
                                        <AssignmentIcon sx={{fontSize: 40, color: '#6366f1', mb: 1}}/>
                                        <Typography variant="h4" sx={{fontWeight: 700, color: '#6366f1'}}>
                                           {exams.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Exams
                                        </Typography>
                                    </CardContent>
                                </StatCard>
                            </Zoom>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Zoom in={true} timeout={700}>
                                <StatCard color="#10b981">
                                    <CardContent sx={{textAlign: 'center'}}>
                                        <QuizIcon sx={{fontSize: 40, color: '#10b981', mb: 1}}/>
                                        <Typography variant="h4" sx={{fontWeight: 700, color: '#10b981'}}>
                                            {questions.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Questions
                                        </Typography>
                                    </CardContent>
                                </StatCard>
                            </Zoom>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Zoom in={true} timeout={900}>
                                <StatCard color="#f59e0b">
                                    <CardContent sx={{textAlign: 'center'}}>
                                        <TrendingUpIcon sx={{fontSize: 40, color: '#f59e0b', mb: 1}}/>
                                        <Typography variant="h4" sx={{fontWeight: 700, color: '#f59e0b'}}>
                                            {statistics.totalSubmissions}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total Submissions
                                        </Typography>
                                    </CardContent>
                                </StatCard>
                            </Zoom>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Zoom in={true} timeout={1100}>
                                <StatCard color="#ec4899">
                                    <CardContent sx={{textAlign: 'center'}}>
                                        <StatsIcon sx={{fontSize: 40, color: '#ec4899', mb: 1}}/>
                                        <Typography variant="h4" sx={{fontWeight: 700, color: '#ec4899'}}>
                                            {statistics.averageScore}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Average Score
                                        </Typography>
                                    </CardContent>
                                </StatCard>
                            </Zoom>
                        </Grid>
                    </Grid>

                    <Card sx={{mb: 3, borderRadius: 3}}>
                        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                            <Tabs
                                value={currentTab}
                                onChange={(e, v) => setCurrentTab(v)}
                                sx={{px: 2}}
                                variant="scrollable"
                                scrollButtons="auto"
                            >
                                <Tab
                                    label="Manage Exams"
                                    icon={<AssignmentIcon/>}
                                    iconPosition="start"
                                />
                                <Tab
                                    label="Question Bank"
                                    icon={<QuizIcon/>}
                                    iconPosition="start"
                                />
                                <Tab
                                    label="Take Exam"
                                    icon={<StartIcon/>}
                                    iconPosition="start"
                                />
                                <Tab
                                    label="Results & Analytics"
                                    icon={<StatsIcon/>}
                                    iconPosition="start"
                                />
                            </Tabs>
                        </Box>

                        {/* Exams Tab */}
                        <TabPanel value={currentTab} index={0}>
                            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h4" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <AssignmentIcon sx={{fontSize: 35}}/>
                                    Exam Management
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<AddIcon/>}
                                    onClick={() => openExamDialog()}
                                    sx={{
                                        background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                                        px: 3, py: 1.5
                                    }}
                                >
                                    Create New Exam
                                </Button>
                            </Box>

                            {/* Filters */}
                            <Card sx={{mb: 3, p: 2, bgcolor: 'grey.50'}}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Class"
                                            size="small"
                                            value={examFilters.className}
                                            onChange={(e) => setExamFilters({
                                                ...examFilters,
                                                className: e.target.value
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Section"
                                            size="small"
                                            value={examFilters.section}
                                            onChange={(e) => setExamFilters({...examFilters, section: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            size="small"
                                            value={examFilters.subject}
                                            onChange={(e) => setExamFilters({...examFilters, subject: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <Button
                                            variant="contained"
                                            onClick={loadExamsFilter}
                                            fullWidth
                                            startIcon={<FilterIcon/>}
                                            sx={{height: 40}}
                                        >
                                            Apply Filters
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Card>

                            {loading ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                    <CircularProgress size={50}/>
                                </Box>
                            ) : (
                                <TableContainer component={Paper} sx={{borderRadius: 3}}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{bgcolor: 'primary.main'}}>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Title</TableCell>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Subject</TableCell>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Class</TableCell>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Duration</TableCell>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Status</TableCell>
                                                <TableCell sx={{color: 'white', fontWeight: 600}}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {exams.map((exam) => (
                                                <TableRow key={exam.id} hover>
                                                    <TableCell>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {exam.topic}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{exam.subject}</TableCell>
                                                    <TableCell>{exam.className} - {exam.section}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            icon={<TimerIcon/>}
                                                            label={`${exam.durationMinutes} min`}
                                                            size="small"
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={exam.status || 'Active'}
                                                            color={(exam.status || 'Active') === 'Active' ? 'success' :
                                                                (exam.status === 'Pending' ? 'warning' :
                                                                    (exam.status === 'Completed' ? 'info' : 'default'))}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{display: 'flex', gap: 1}}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => openExamDialog(exam)}
                                                            >
                                                                <EditIcon/>
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={() => handleStartExam(exam.id)}
                                                            >
                                                                <StartIcon/>
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteExam(exam.id)}
                                                            >
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {exams.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{py: 3}}>
                                                        <Typography variant="body1" color="text.secondary">
                                                            No exams found. Create a new exam to get started.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </TabPanel>

                        {/* Question Bank Tab */}
                        <TabPanel value={currentTab} index={1}>
                            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h4" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <QuizIcon sx={{fontSize: 35}}/>
                                    Question Bank
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<AddIcon/>}
                                    onClick={() => openQuestionDialog(null)}
                                    sx={{
                                        background: 'linear-gradient(45deg, #10b981, #34d399)',
                                        px: 3, py: 1.5
                                    }}
                                >
                                    Add New Question
                                </Button>
                            </Box>

                            {/* Filters */}
                            <Card sx={{mb: 3, p: 2, bgcolor: 'grey.50'}}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            size="small"
                                            value={questionFilters.subject}
                                            onChange={(e) => setQuestionFilters({
                                                ...questionFilters,
                                                subject: e.target.value
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Topic"
                                            size="small"
                                            value={questionFilters.topic}
                                            onChange={(e) => setQuestionFilters({
                                                ...questionFilters,
                                                topic: e.target.value
                                            })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Class"
                                            size="small"
                                            value={questionFilters.className}
                                            onChange={(e) => setQuestionFilters({
                                                ...questionFilters,
                                                className: e.target.value
                                            })}
                                        />
                                    </Grid>
                                          <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Section"
                                            size="small"
                                            value={questionFilters.section}
                                            onChange={(e) => setQuestionFilters({...questionFilters, section: e.target.value})}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <Button
                                            variant="contained"
                                            onClick={loadQuestionsFilter}
                                            fullWidth
                                            startIcon={<FilterIcon/>}
                                            sx={{height: 40}}
                                        >
                                            Apply Filters
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Card>

                            {loading ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                    <CircularProgress size={50}/>
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {questions?.map((question) => (
                                        <Grid item xs={12} key={question?.id}>
                                            <Card sx={{borderRadius: 2, boxShadow: 2}}>
                                                <CardContent>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'flex-start',
                                                        mb: 2
                                                    }}>
                                                        <Typography variant="h6" sx={{fontWeight: 600}}>
                                                            {question?.questionText}
                                                        </Typography>
                                                        <Chip
                                                            label={`${question.marks} ${question.marks > 1 ? 'marks' : 'mark'}`}
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Box>

                                                    <Typography variant="body2" sx={{color: 'text.secondary', mb: 2}}>
                                                        Subject: {question?.subject} |
                                                        Topic: {question?.topic || 'General'}
                                                    </Typography>

                                                    <Grid container spacing={2} sx={{mb: 2}}>
                                                        {['A', 'B', 'C', 'D'].map((option, index) => (
                                                            question[`option${option}`] && (
                                                                <Grid item xs={12} sm={6} key={option}>
                                                                    <Paper
                                                                        sx={{
                                                                            p: 1,
                                                                            bgcolor: question.correctAnswer === index ? 'success.light' : 'background.paper',
                                                                            border: '1px solid',
                                                                            borderColor: question.correctAnswer === index ? 'success.main' : 'grey.300',
                                                                            borderRadius: 2,
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            component="span"
                                                                            sx={{
                                                                                fontWeight: question.correctAnswer === index ? 700 : 400
                                                                            }}
                                                                        >
                                                                            {option}. {question[`option${option}`]}
                                                                        </Typography>
                                                                        {question.correctAnswer === index && (
                                                                            <CheckIcon
                                                                                sx={{
                                                                                    ml: 'auto',
                                                                                    color: 'success.dark',
                                                                                    fontSize: 20
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Paper>
                                                                </Grid>
                                                            )
                                                        ))}
                                                    </Grid>

                                                    {question.explanation && (
                                                        <Box sx={{mt: 1, p: 1.5, bgcolor: 'info.50', borderRadius: 2}}>
                                                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                                                Explanation: {question.explanation}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                                <CardActions>
                                                    <Button
                                                        size="small"
                                                        startIcon={<EditIcon/>}
                                                        onClick={() => openQuestionDialog(question)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        startIcon={<DeleteIcon/>}
                                                        color="error"
                                                        onClick={() => handleDeleteQuestion(question.id)}
                                                    >
                                                        Delete
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    ))}

                                    {questions?.length === 0 && (
                                        <Grid item xs={12}>
                                            <Paper sx={{p: 4, textAlign: 'center'}}>
                                                <Typography variant="body1" color="text.secondary">
                                                    No questions found. Add new questions to your bank.
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </TabPanel>

                        {/* Take Exam Tab */}
                        <TabPanel value={currentTab} index={2}>
                            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h4" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <StartIcon sx={{fontSize: 35}}/>
                                    Available Exams
                                </Typography>
                            </Box>

                            {loading ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                                    <CircularProgress size={50}/>
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {exams.map((exam) => (
                                        <Grid item xs={12} sm={6} md={4} key={exam.id}>
                                            <Zoom in={true} style={{transitionDelay: '100ms'}}>
                                                <Card sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    borderRadius: 3
                                                }}>
                                                    <GradientCard>
                                                        <CardContent>
                                                            <Typography variant="h5" fontWeight={700}>
                                                                {exam.topic}
                                                            </Typography>
                                                            <Typography variant="body2" sx={{opacity: 0.8, mb: 1}}>
                                                                {exam.subject} | Class {exam.className} - {exam.section}
                                                            </Typography>
                                                        </CardContent>
                                                    </GradientCard>
                                                    <CardContent sx={{flexGrow: 1}}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            mb: 2
                                                        }}>
                                                            <Chip
                                                                icon={<TimerIcon/>}
                                                                label={`${exam.durationMinutes} min`}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                            <Chip
                                                                icon={<AssignmentIcon/>}
                                                                label={`${exam.totalMarks} marks`}
                                                                variant="outlined"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" paragraph>
                                                            {exam.description || 'No description available.'}
                                                        </Typography>
                                                    </CardContent>
                                                    <CardActions sx={{p: 2, pt: 0}}>
                                                        <Button
                                                            variant="contained"
                                                            fullWidth
                                                            startIcon={<StartIcon/>}
                                                            onClick={() => handleStartExam(exam.id)}
                                                            sx={{
                                                                background: 'linear-gradient(45deg, #6366f1, #ec4899)',
                                                                py: 1
                                                            }}
                                                        >
                                                            Start Exam
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Zoom>
                                        </Grid>
                                    ))}

                                    {exams.length === 0 && (
                                        <Grid item xs={12}>
                                            <Paper sx={{p: 4, textAlign: 'center'}}>
                                                <Typography variant="body1" color="text.secondary">
                                                    No exams available. Please check back later.
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </TabPanel>

                        {/* Results Tab */}
                        <TabPanel value={currentTab} index={3}>
                            <Box sx={{mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Typography variant="h4" sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <StatsIcon sx={{fontSize: 35}}/>
                                    Results & Analytics
                                </Typography>
                            </Box>

                            {/* Analytics charts and tables would go here */}
                            <Paper sx={{p: 3, mb: 3, borderRadius: 3}}>
                                <Typography variant="h6" gutterBottom>
                                    Performance Overview
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Detailed analytics and results will be displayed here.
                                </Typography>
                                <LinearProgress variant="determinate" value={70}
                                                sx={{height: 10, borderRadius: 5, mb: 2}}/>
                            </Paper>
                        </TabPanel>
                    </Card>
                </Container>
            </Box>

            {/* Create/Edit Exam Dialog */}
            <Dialog open={examDialog.open} onClose={() => setExamDialog({open: false, exam: null})} maxWidth="md"
                    fullWidth>
                <DialogTitle sx={{bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center'}}>
                    <AssignmentIcon sx={{mr: 1}}/>
                    {examDialog.exam ? 'Edit Exam' : 'Create New Exam'}
                    <IconButton
                        sx={{ml: 'auto', color: 'white'}}
                        onClick={() => setExamDialog({open: false, exam: null})}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
              <DialogContent sx={{py: 3}}>
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Exam Name"
        value={examForm.examName}
        onChange={(e) => setExamForm({...examForm, examName: e.target.value})}
        required
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Subject"
        value={examForm.subject}
        onChange={(e) => setExamForm({...examForm, subject: e.target.value})}
        required
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Topic"
        value={examForm.topic}
        onChange={(e) => setExamForm({...examForm, topic: e.target.value})}
      />
    </Grid>
    
    <Grid item xs={12} sm={4}>
        <FormControl fullWidth style={{marginBottom: '20px'}}>
                                 <InputLabel>Select Class</InputLabel>
                                 <Select
                                     label="class"
                                     name="className"
                                     value={examForm.className}
                                          onChange={(e) => setExamForm({...examForm, className: e.target.value})}
                                  
                                 >
                                     {classSections && classSections.length > 0 ? (
                                         classSections.map((cls) => (
                                             <MenuItem key={cls.id} value={cls.name}>
                                                 {cls.name}
                                             </MenuItem>
                                         ))
                                     ) : (
                                         <MenuItem disabled>No classes available</MenuItem>
                                     )}
                                 </Select>
                             </FormControl>
    </Grid>
    
    <Grid item xs={12} sm={4}>
     <FormControl fullWidth>
                                 <InputLabel>Select Section</InputLabel>
                                 <Select
                                     label="section"
                                     name="section"
                                     value={examForm.section}
                                          onChange={(e) => setExamForm({...examForm, section: e.target.value})}

                                     disabled={!examForm.className}
                                 >
                                     {examForm.className ? (
                                         classSections
                                             .find((cls) => cls.name === examForm.className)
                                             ?.sections?.map((sec) => (
                                                 <MenuItem key={sec.id} value={sec.name}>
                                                     {sec.name}
                                                 </MenuItem>
                                             )) || <MenuItem disabled>No sections available</MenuItem>
                                     ) : (
                                         <MenuItem disabled>Please select a class first</MenuItem>
                                     )}
                                 </Select>
                             </FormControl>
    </Grid>
      
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Exam Date"
        type="date"
        value={examForm.examDate}
        onChange={(e) => setExamForm({...examForm, examDate: e.target.value})}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Start Time"
        type="time"
        value={examForm.startTime}
        onChange={(e) => setExamForm({...examForm, startTime: e.target.value})}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="End Time"
        type="time"
        value={examForm.endTime}
        onChange={(e) => setExamForm({...examForm, endTime: e.target.value})}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Duration (minutes)"
        type="number"
        value={examForm.durationMinutes}
        onChange={(e) => setExamForm({...examForm, durationMinutes: e.target.value})}
        required
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Total Marks"
        type="number"
        value={examForm.totalMarks}
        onChange={(e) => setExamForm({...examForm, totalMarks: e.target.value})}
        required
      />
    </Grid>
    
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Passing Marks"
        type="number"
        value={examForm.passingMarks}
        onChange={(e) => setExamForm({...examForm, passingMarks: e.target.value})}
      />
    </Grid>
    
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Description"
        multiline
        rows={3}
        value={examForm.description}
        onChange={(e) => setExamForm({...examForm, description: e.target.value})}
      />
    </Grid>
    
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Instructions"
        multiline
        rows={3}
        value={examForm.instructions}
        onChange={(e) => setExamForm({...examForm, instructions: e.target.value})}
      />
    </Grid>
  </Grid>
</DialogContent>
                <DialogActions sx={{px: 3, pb: 3}}>
                    <Button
                        onClick={() => setExamDialog({open: false, exam: null})}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        onClick={examDialog.exam ? handleUpdateExam : handleCreateExam}
                        color="primary"
                    >
                        {examDialog.exam ? 'Update Exam' : 'Create Exam'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Question Dialog */}
            <Dialog open={questionDialog.open} onClose={() => setQuestionDialog({open: false, question: null})}
                    maxWidth="md" fullWidth>
                <DialogTitle sx={{bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center'}}>
                    <QuizIcon sx={{mr: 1}}/>
                    {questionDialog.question ? 'Edit Question' : 'Create New Question'}
                    <IconButton
                        sx={{ml: 'auto', color: 'white'}}
                        onClick={() => setQuestionDialog({open: false, question: null})}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
             <DialogContent sx={{ py: 3 }}>
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Question"
        multiline
        rows={2}
        value={questionForm.questionText}
        onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
        required
      />
    </Grid>

    {/* Session, Class, Section */}
    <Grid item xs={12} sm={4}>
  <FormControl fullWidth variant="outlined">
    <InputLabel style={{ minWidth: "120px" }}>Class</InputLabel>
    <Select
      label="Class"
      name="className"
      value={questionForm.className}
      onChange={(e) =>
        setQuestionForm({ ...questionForm, className: e.target.value })
      }
    >
      {classSections && classSections.length > 0 ? (
        classSections.map((cls) => (
          <MenuItem key={cls.id} value={cls.name}>
            {cls.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No classes available</MenuItem>
      )}
    </Select>
  </FormControl>
</Grid>

<Grid item xs={12} sm={4}>
  <FormControl fullWidth variant="outlined">
    <InputLabel style={{ minWidth: "120px" }}>Section</InputLabel>
    <Select
      label="Section"
      name="section"
      value={questionForm.section}
      onChange={(e) =>
        setQuestionForm({ ...questionForm, section: e.target.value })
      }
      disabled={!questionForm.className}
    >
      {questionForm.className ? (
        classSections
          .find((cls) => cls.name === questionForm.className)
          ?.sections?.map((sec) => (
            <MenuItem key={sec.id} value={sec.name}>
              {sec.name}
            </MenuItem>
          )) || <MenuItem disabled>No sections available</MenuItem>
      ) : (
        <MenuItem disabled>Please select a class first</MenuItem>
      )}
    </Select>
  </FormControl>
</Grid>

    {/* Subject and Topic */}
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Subject"
        value={questionForm.subject}
        onChange={(e) => setQuestionForm({ ...questionForm, subject: e.target.value })}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Topic"
        value={questionForm.topic}
        onChange={(e) => setQuestionForm({ ...questionForm, topic: e.target.value })}
      />
    </Grid>

    {/* Options A-D */}
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Option A"
        value={questionForm.optionA}
        onChange={(e) => setQuestionForm({ ...questionForm, optionA: e.target.value })}
        required
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Option B"
        value={questionForm.optionB}
        onChange={(e) => setQuestionForm({ ...questionForm, optionB: e.target.value })}
        required
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Option C"
        value={questionForm.optionC}
        onChange={(e) => setQuestionForm({ ...questionForm, optionC: e.target.value })}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        label="Option D"
        value={questionForm.optionD}
        onChange={(e) => setQuestionForm({ ...questionForm, optionD: e.target.value })}
      />
    </Grid>

    {/* Correct Answer - store index 0..3 */}
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <InputLabel id="correct-answer-label">Correct Answer</InputLabel>
        <Select
          labelId="correct-answer-label"
          value={questionForm.correctAnswer ?? ''}
          label="Correct Answer"
          onChange={(e) =>
            setQuestionForm({ ...questionForm, correctAnswer: e.target.value === '' ? '' : parseInt(e.target.value, 10) })
          }
        >
          <MenuItem value={0}>A</MenuItem>
          <MenuItem value={1}>B</MenuItem>
          <MenuItem value={2}>C</MenuItem>
          <MenuItem value={3}>D</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    {/* Marks, Negative Marks, Time Limit */}
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Marks"
        type="number"
        value={questionForm.marks}
        onChange={(e) => setQuestionForm({ ...questionForm, marks: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
        required
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Negative Marks"
        type="number"
        value={questionForm.negativeMarks}
        onChange={(e) => setQuestionForm({ ...questionForm, negativeMarks: e.target.value === '' ? '' : parseFloat(e.target.value) })}
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Time Limit (seconds)"
        type="number"
        value={questionForm.timeLimitSeconds}
        onChange={(e) => setQuestionForm({ ...questionForm, timeLimitSeconds: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
      />
    </Grid>

    {/* Difficulty and Question Type (enums) */}
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <InputLabel id="difficulty-label">Difficulty</InputLabel>
        <Select
          labelId="difficulty-label"
          value={questionForm.difficultyLevel || 'MEDIUM'}
          label="Difficulty"
          onChange={(e) => setQuestionForm({ ...questionForm, difficultyLevel: e.target.value })}
        >
          <MenuItem value="EASY">EASY</MenuItem>
          <MenuItem value="MEDIUM">MEDIUM</MenuItem>
          <MenuItem value="HARD">HARD</MenuItem>
        </Select>
      </FormControl>
    </Grid>

    <Grid item xs={12} sm={6}>
      <FormControl fullWidth>
        <InputLabel id="qtype-label">Question Type</InputLabel>
        <Select
          labelId="qtype-label"
          value={questionForm.questionType || 'MULTIPLE_CHOICE'}
          label="Question Type"
          onChange={(e) => setQuestionForm({ ...questionForm, questionType: e.target.value })}
        >
          <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
          <MenuItem value="TRUE_FALSE">True / False</MenuItem>
          <MenuItem value="Theory">Theory</MenuItem>

          {/* add other types if you have them */}
        </Select>
      </FormControl>
    </Grid>

    {/* Explanation */}
    <Grid item xs={12}>
      <TextField
        fullWidth
        label="Explanation (Optional)"
        multiline
        rows={2}
        value={questionForm.explanation}
        onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
      />
    </Grid>

    {/* Image URL, createdBy, approvedBy */}
    <Grid item xs={12} sm={4}>
      <TextField
        fullWidth
        label="Image URL"
        value={questionForm.imageUrl}
        onChange={(e) => setQuestionForm({ ...questionForm, imageUrl: e.target.value })}
      />
    </Grid>

    {/* isApproved & isActive switches */}
    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Switch
            checked={!!questionForm.isApproved}
            onChange={(e) => setQuestionForm({ ...questionForm, isApproved: e.target.checked })}
          />
        }
        label="Is Approved"
      />
      <FormControlLabel
        sx={{ ml: 2 }}
        control={
          <Switch
            checked={!!questionForm.isActive}
            onChange={(e) => setQuestionForm({ ...questionForm, isActive: e.target.checked })}
          />
        }
        label="Is Active"
      />
    </Grid>

    {/* usageCount & successRate (display/read-only) */}
      </Grid>
</DialogContent>

                <DialogActions sx={{px: 3, pb: 3}}>
                    <Button
                        onClick={() => setQuestionDialog({open: false, question: null})}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        onClick={handleCreateQuestion}
                        color="success"
                    >
                        {questionDialog.question ? 'Update Question' : 'Create Question'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Exam Taking Dialog */}
                 <Dialog
      open={examTakingDialog.open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: { 
          height: '80vh',
          maxHeight: '700px'
        }
      }}
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {exams?.title || 'Exam'}
          </Typography>
          <Chip
            icon={<TimerIcon />}
            label={formatTime(timeLeft)}
            color={timeLeft < 300 ? "error" : "default"}
            variant="filled"
            sx={{ 
              fontWeight: 700, 
              bgcolor: timeLeft < 300 ? 'error.dark' : 'rgba(255,255,255,0.2)',
              color: 'white'
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Questions Navigation Sidebar */}
        <Paper sx={{ width: 250, flexShrink: 0, overflow: 'auto', borderRadius: 0 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" gutterBottom>
              Questions
            </Typography>
            <LinearProgress
              variant="determinate"
              value={questionsCount > 0 ? (Object.keys(answers).length / questionsCount) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Answered: {Object.keys(answers).length} / {questionsCount}
            </Typography>
          </Box>
          
          {questionsCount > 0 ? (
            <List dense sx={{ p: 1 }}>
              {questions.map((q, index) => (
                <ListItem key={q.id || index} disablePadding>
                  <ListItemButton
                    selected={currentQuestion === index}
                    onClick={() => handleQuestionSelect(index)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main'
                        }
                      }
                    }}
                  >
                    <ListItemText 
                      primary={`Question ${index + 1}`}
                      secondary={isAnswered(q.id) ? "Answered" : "Not answered"}
                    />
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: isAnswered(q.id) ? 'success.main' : 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        color: isAnswered(q.id) ? 'white' : 'text.primary'
                      }}
                    >
                      {index + 1}
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
              No questions available
            </Typography>
          )}
        </Paper>

        {/* Question Content */}
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={50} />
            </Box>
          ) : questionsCount > 0 && currentQuestionData ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Question {currentQuestion + 1} of {questionsCount}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((currentQuestion + 1) / questionsCount) * 100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>

              <Typography variant="h6" gutterBottom>
                {currentQuestionData.questionText}
              </Typography>

              <RadioGroup
                value={answers[currentQuestionData.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestionData.id, e.target.value)}
                sx={{ mt: 2 }}
              >
                {['A', 'B', 'C', 'D'].map((option) => {
                  const optionValue = currentQuestionData[`option${option}`];
                  
                  if (!optionValue) return null;

                  return (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={<Radio />}
                      label={`${option}. ${optionValue}`}
                      sx={{
                        display: 'block',
                        mt: 1,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    />
                  );
                })}
              </RadioGroup>
            </>
          ) : (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mt: 4 }}>
              No questions available for this exam.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          disabled={currentQuestion === 0}
          onClick={handlePrevious}
        >
          Previous
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentQuestion < questionsCount - 1 && (
            <Button
              endIcon={<ArrowForward />}
              variant="contained"
              onClick={handleNext}
            >
              Next
            </Button>
          )}
          
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmitExam}
          >
            Submit Exam
          </Button>
        </Box>
      </DialogActions>
    </Dialog>


            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({...snackbar, open: false})}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            >
                <Alert
                    onClose={() => setSnackbar({...snackbar, open: false})}
                    severity={snackbar.severity}
                    sx={{width: '100%'}}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
}
